import { StudyLearningJourney } from './StudyLearningJourney';
import { StudyDemoWorkingResult } from '@/pages/try/studyExperience/StudyDemoStudyResults';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { StudyWorkingExampleLink } from '@/pages/try/studyExperience/StudyWorkingExampleLink';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Pencil, LockKeyhole } from 'lucide-react';
import { getKidSub } from '@/auth/authStore';
import { StudyShell } from './StudyShell';
import { TutorAvatar } from '@/components/tutor/TutorAvatar';
import { listDeviceWorkingDrafts } from './workingDeviceDrafts';
import { AcademyRichText } from '../academy/AcademyRichText';
import { WorkingCanvasPanel } from './WorkingCanvasPanel';
import {
  getSavedWorking,
  listWorkingCanvases,
  WORKING_LIBRARY_QUERY_KEY,
  type WorkingSummary,
} from './workingCanvasApi';
const contexts = {
  free: 'My question',
  practice: 'Practice',
  study: 'Study material',
  material: 'Material working',
  mock: 'Mock exam',
};
function savedDate(date: string) {
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(date),
  );
}
export function StudyWorkingLibraryPage() {
  const [kind, setKind] = useState('');
  const drafts = listDeviceWorkingDrafts(getKidSub());
  const pages = useInfiniteQuery({
    queryKey: [...WORKING_LIBRARY_QUERY_KEY, getKidSub(), kind],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listWorkingCanvases(kind, pageParam),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
    // Returning from an editor must include its new account page, even while
    // the app's previous library cache is still considered fresh.
    refetchOnMount: 'always',
  });
  const items = pages.isError ? [] : (pages.data?.pages.flatMap((page) => page.items) ?? []);
  const deviceChanges = (item: WorkingSummary) =>
    item.context.kind === 'free'
      ? drafts.find(
          (draft) => draft.pageId === item.id && draft.questionId === item.context.question_id,
        )
      : undefined;
  const deviceDrafts = drafts.filter(
    (draft) => !items.some((item) => deviceChanges(item) === draft),
  );
  return (
    <StudyShell>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="section-heading">My working</h1>
        <Link
          to="/learn/study"
          className="inline-flex min-h-11 items-center gap-2 text-sm font-bold text-brand-sky"
        >
          <ArrowLeft size={16} aria-hidden="true" /> Study home
        </Link>
      </div>
      <p className="mt-3 max-w-2xl text-slate2">
        Your saved calculations, diagrams and reasoning. Reopen a page to continue. Practice and
        Assessment keep their own submitted answers and feedback.
      </p>
      <StudyLearningJourney active="working" />
      {isLocalStudyExperience() && <StudyDemoWorkingResult />}
      <section className="mt-5 flex flex-col gap-4 rounded-3xl border border-hairline bg-wash-sky p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <TutorAvatar size="large" expression="greeting" />
          <div>
            <h2 className="text-xl font-extrabold">Start a separate working page</h2>
            <p className="mt-1 max-w-lg text-sm text-slate2">
              Write your own question and calculation steps. This page saves your reasoning; it does
              not submit an Assessment answer.
            </p>
            <p className="mt-2 text-xs text-slate2">
              {isLocalStudyExperience()
                ? 'Demo edits stay in this tab. Tutor examples are pre-generated.'
                : 'Mathematics · Writing is separate from AI help. Preview the Star price before sending.'}
            </p>
          </div>
        </div>
        <Link className="btn-pill-primary shrink-0 text-center" to="/learn/study/working/new">
          Start a blank working page
        </Link>
      </section>
      <p className="mt-3 text-sm text-slate2">
        Want a practice question?{' '}
        <Link
          className="font-bold text-brand-sky"
          to="/learn/study/subjects/mathematics?tab=practice"
        >
          Browse maths practice
        </Link>
      </p>
      {deviceDrafts.length > 0 && (
        <section className="mt-6" aria-label="Device drafts">
          <h2 className="text-lg font-extrabold">On this device</h2>
          <p className="mt-1 text-xs text-slate2">
            These drafts have unsynced steps. They stay on this browser and are cleared when you
            sign out.
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {deviceDrafts.map((draft) => (
              <Link
                key={draft.questionId}
                className="card-base"
                to={`/learn/study/working/new?draft=${draft.questionId}`}
              >
                <p className="text-xs font-bold text-slate2">
                  {draft.synced ? 'Changes on this device' : 'Device draft · not saved to account'}
                </p>
                <p className="mt-2 line-clamp-2 font-bold">{draft.question}</p>
                <span className="mt-3 inline-flex min-h-11 items-center gap-2 font-bold">
                  Continue draft <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
      <h2 className="mt-8 text-xl font-extrabold">
        {isLocalStudyExperience() ? 'Saved in this demo session' : 'Saved to your account'}
      </h2>
      <p className="mt-2 text-xs text-slate2">
        Pages from your own questions, practice, materials and mock exams appear here after “Saved”.
      </p>
      <label className="mt-6 inline-flex items-center gap-3 text-sm font-bold">
        Show
        <select
          aria-label="Working context"
          value={kind}
          onChange={(event) => setKind(event.target.value)}
          className="min-h-11 rounded-full border border-hairline bg-canvas-pure px-4"
        >
          <option value="">All working</option>
          <option value="free">My questions</option>
          <option value="practice">Practice</option>
          <option value="study">Study materials</option>
          <option value="mock">Mock exams</option>
        </select>
      </label>
      {pages.isPending && (
        <p role="status" className="mt-8">
          Loading your working…
        </p>
      )}
      {pages.isError && (
        <div role="alert" className="card-base mt-6">
          <p>Your saved pages could not load. Check your connection and try again.</p>
          <button className="btn-pill-secondary mt-4" onClick={() => void pages.refetch()}>
            Try again
          </button>
        </div>
      )}
      {!pages.isPending && !pages.isError && !items.length && (
        <section className="card-base mt-6">
          <Pencil className="text-brand-sky" />
          <h2 className="mt-3 text-xl font-extrabold">
            {kind ? 'No saved working in this category' : 'No account pages yet'}
          </h2>
          <p className="mt-3 text-slate2">
            Choose Start working above to open the handwriting page and Tutor. You can begin without
            a saved page.
          </p>
        </section>
      )}
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {!pages.isError &&
          items.map((item) => {
            const changes = deviceChanges(item);
            return (
              <Link
                key={item.id}
                to={`/learn/study/working/${item.id}`}
                className="card-base flex flex-col hover:border-brand-sky"
              >
                <div className="flex items-center justify-between gap-2 text-xs font-bold text-slate2">
                  <span>{contexts[item.context.kind]}</span>
                  <span className="flex items-center gap-1">
                    {item.locked ? <LockKeyhole size={14} /> : <Pencil size={14} />}{' '}
                    {item.locked ? 'Submitted · read only' : 'Editable'}
                  </span>
                </div>
                <h2 className="mt-4 text-base font-extrabold">{item.title}</h2>
                {changes && (
                  <p className="mt-2 text-xs font-bold text-brand-sky">
                    {item.locked
                      ? 'Unsynced draft available for export'
                      : 'Unsynced changes on this device'}
                  </p>
                )}
                {item.question && (
                  <div className="mt-2 line-clamp-3 text-sm text-ink">
                    <AcademyRichText
                      text={changes && !item.locked ? changes.question : item.question}
                    />
                  </div>
                )}
                <p className="mt-4 text-xs text-slate2">Saved {savedDate(item.updated_at)}</p>
                {item.retention_until && (
                  <p className="mt-1 text-xs text-slate2">
                    Available until {savedDate(item.retention_until)}
                  </p>
                )}
                <span className="mt-5 flex items-center gap-2 text-sm font-extrabold text-ink">
                  {item.locked ? 'View working' : 'Continue working'}
                  <ArrowRight size={16} />
                </span>
              </Link>
            );
          })}
      </div>
      {pages.hasNextPage && (
        <button
          className="btn-pill-secondary mt-6"
          disabled={pages.isFetchingNextPage}
          onClick={() => void pages.fetchNextPage()}
        >
          {pages.isFetchingNextPage ? 'Loading…' : 'Load more working'}
        </button>
      )}
    </StudyShell>
  );
}
export function StudySavedWorkingPage() {
  const { id = '' } = useParams();
  const page = useQuery({
    queryKey: [...WORKING_LIBRARY_QUERY_KEY, getKidSub(), 'page', id],
    queryFn: () => getSavedWorking(id),
    retry: false,
  });
  return (
    <StudyShell>
      <Link
        to="/learn/study/working"
        className="inline-flex min-h-11 items-center gap-2 text-sm font-extrabold"
      >
        <ArrowLeft size={16} />
        My working
      </Link>
      {page.isPending && (
        <p role="status" className="mt-6">
          Loading saved working…
        </p>
      )}
      {page.isError && (
        <section role="alert" className="card-base mt-6">
          <h1 className="text-xl font-extrabold">This page could not open</h1>
          <p className="mt-3 text-slate2">
            It may have expired or been deleted, or your connection may be unavailable. Return to My
            working or try again.
          </p>
          <button className="btn-pill-secondary mt-4" onClick={() => void page.refetch()}>
            Try again
          </button>
        </section>
      )}
      {page.data && !page.isError && (
        <>
          <p className="eyebrow eyebrow-sky mt-5">{contexts[page.data.context.kind]}</p>
          <h1 className="section-heading mt-3">{page.data.title}</h1>
          {page.data.question && page.data.context.kind !== 'free' && (
            <div className="mt-4 max-w-3xl text-lg">
              <AcademyRichText text={page.data.question} />
            </div>
          )}
          <p className="mt-3 text-sm text-slate2">
            {page.data.locked
              ? 'Submitted working is read only. You can export a copy.'
              : 'Continue your steps. Changes save to the same page.'}
          </p>
          {page.data.context.kind === 'study' && (
            <Link
              className="mt-3 inline-flex text-sm font-bold text-brand-sky"
              to={`/learn/study/sessions/${encodeURIComponent(page.data.context.session_id!)}`}
            >
              Open study material
            </Link>
          )}
          {page.data.content.text.trim() && (
            <article
              aria-label="Saved working content"
              className="mt-5 rounded-2xl border border-hairline bg-canvas-pure p-5"
            >
              <h2 className="text-xl font-extrabold">Saved steps and result</h2>
              <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">
                <AcademyRichText text={page.data.content.text} />
              </div>
            </article>
          )}
          {page.data.context.kind === 'practice' && (
            <Link
              className="mt-3 inline-block min-h-11 font-bold underline"
              to="/learn/study/subjects/mathematics?tab=practice"
            >
              Back to maths practice
            </Link>
          )}
          <StudyWorkingExampleLink id={page.data.id} />
          <WorkingCanvasPanel
            key={page.data.id}
            savedPageId={page.data.id}
            context={page.data.context}
            question={page.data.question}
            contextLabel={contexts[page.data.context.kind]}
            locked={page.data.locked}
            onSaved={() => void page.refetch()}
          />
        </>
      )}
    </StudyShell>
  );
}
